import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';

// Components
import MapTest from "./MapTest";
import Login from './components/Login';
import Register from './components/Register';
import AdminDashboard from './pages/admin/AdminDashboard';
import CreateUserAccount from './pages/admin/CreateUserAccount';
import UserList from './pages/admin/UserList';
import EditUser from './pages/admin/EditUser';
import TransporteurLimits from './pages/admin/TransporteurLimits';
import TransporteurDashboard from './pages/transporteur/Dashboard';
import AgentControlCenter from './pages/agent/AgentControlCenter';
import ExpediteurDashboard from './pages/expediteur/ExpediteurDashboard';
import AddTransporteur from './pages/expediteur/AddTransporteur';
import ContractView from './pages/expediteur/ContractView';
import ContractsList from './pages/expediteur/ContractsList';
import ChauffeurList from './pages/transporteur/ChauffeurList';
import ChauffeurForm from './pages/transporteur/ChauffeurForm';
import VehiculeList from './pages/transporteur/VehiculeList';
import VehiculeForm from './pages/transporteur/VehiculeForm';
import RemorqueList from './pages/transporteur/RemorqueList';
import RemorqueForm from './pages/transporteur/RemorqueForm';
import MissionList from './pages/transporteur/MissionList';
import MissionForm from './pages/transporteur/MissionForm';
import MissionTemplateList from './pages/transporteur/MissionTemplateList';
import MissionTemplateForm from './pages/transporteur/MissionTemplateForm';
import MissionSettings from './pages/transporteur/MissionSettings';
import AssignMission from './pages/transporteur/AssignMission';
import SubcontractMission from './pages/transporteur/SubcontractMission';
import GanttChartPage from './pages/transporteur/GanttChartPage';
import ClientOffers from './pages/transporteur/ClientOffers';
import OfferDetail from './pages/transporteur/OfferDetail';
import PendingOffers from './pages/transporteur/PendingOffers';
import Subcontractors from './pages/transporteur/Subcontractors';
import SubcontractorDetail from './pages/transporteur/SubcontractorDetail';
import SubcontractedMissions from './pages/transporteur/SubcontractedMissions';
import SubcontractedMissionDetail from './pages/transporteur/SubcontractedMissionDetail';
import Profile from './components/Profile';
import DashboardLayout from './components/layout/DashboardLayout';
import { UserProvider, useUser } from './context/UserContext';
import { FilterProvider } from './context/FilterContext';
import InvoiceList from './pages/transporteur/InvoiceList';
import InvoiceCreate from './pages/transporteur/InvoiceCreate';
import AutomaticInvoice from './pages/transporteur/AutomaticInvoice';
import GpsHistory from './pages/transporteur/gps/GpsHistory';

import ProtectedRoute from './components/ProtectedRoute';
import PublicRoute from './components/PublicRoute';
import MessagerieInterne from './pages/transporteur/MessagerieInterne';
import ChauffeurDetails from './pages/transporteur/ChauffeurDetails';
import ResponsableTransporteurs from './pages/transporteur/ResponsableTransporteurs';
import ClientList from './pages/transporteur/ClientList';
import ClientForm from './pages/transporteur/ClientForm';
import LandingPage from './pages/public/LandingPage';
import ContactPage from './pages/public/ContactPage';
import MentionsLegales from './pages/public/MentionsLegales';
import Confidentialite from './pages/public/Confidentialite';
import CGU from './pages/public/CGU';
import PlanificationIntelligente from './pages/public/solutions/PlanificationIntelligente';
import SuiviTempsReel from './pages/public/solutions/SuiviTempsReel';
import DocumentsCentralises from './pages/public/solutions/DocumentsCentralises';
import RapportsAnalytics from './pages/public/solutions/RapportsAnalytics';
import GestionRessources from './pages/public/solutions/GestionRessources';
import AiAgentCenter from './pages/public/solutions/AiAgentCenter';

// Reports
import GeneralReports from './pages/transporteur/reports/GeneralReports';
import SubcontractingReports from './pages/transporteur/reports/SubcontractingReports';
import DriverReports from './pages/transporteur/reports/DriverReports';
import VehicleReports from './pages/transporteur/reports/VehicleReports';

import BillingReports from './pages/transporteur/reports/BillingReports';

// use dedicated ExpediteurDashboard file

const AppRoutes = React.memo(() => {
  const { userType, token } = useUser();

  // Always redirect to login first
  const defaultRoute = '/login';

  return (
    <Routes>
      {/* Public Routes */}

      <Route
        path="/map-test"
        element={<MapTest />}
      />

      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <Register />
          </ProtectedRoute>
        }
      />

      {/* Admin Dashboard */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users/create"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <CreateUserAccount />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <UserList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users/edit/:type/:id"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <EditUser />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/create-account"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <CreateUserAccount />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/transporteurs/limits"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <TransporteurLimits />
          </ProtectedRoute>
        }
      />

      {/* Protected Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute allowedRoles={['transporteur', 'responsable_transporteur', 'expediteur']}>
            {userType === 'transporteur' || userType === 'responsable_transporteur'
              ? <TransporteurDashboard />
              : <ExpediteurDashboard />}
          </ProtectedRoute>
        }
      />

      {/* Agent Center - Human in the Loop */}
      <Route
        path="/agent-center"
        element={
          <ProtectedRoute allowedRoles={['transporteur', 'responsable_transporteur', 'admin']}>
            <AgentControlCenter />
          </ProtectedRoute>
        }
      />
      <Route
        path="/chauffeurs"
        element={
          <ProtectedRoute allowedRoles={['transporteur', 'responsable_transporteur']}>
            <ChauffeurList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/chauffeurs/create"
        element={
          <ProtectedRoute allowedRoles={['transporteur', 'responsable_transporteur']}>
            <ChauffeurForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/chauffeurs/edit/:id"
        element={
          <ProtectedRoute allowedRoles={['transporteur', 'responsable_transporteur']}>
            <ChauffeurForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/chauffeurs/view/:id"
        element={
          <ProtectedRoute allowedRoles={['transporteur', 'responsable_transporteur']}>
            <ChauffeurDetails />
          </ProtectedRoute>
        }
      />

      {/* Client Management Routes */}
      <Route
        path="/clients"
        element={
          <ProtectedRoute allowedRoles={['transporteur', 'responsable_transporteur']}>
            <ClientList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/clients/create"
        element={
          <ProtectedRoute allowedRoles={['transporteur', 'responsable_transporteur']}>
            <ClientForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/clients/edit/:id"
        element={
          <ProtectedRoute allowedRoles={['transporteur', 'responsable_transporteur']}>
            <ClientForm />
          </ProtectedRoute>
        }
      />



      {/* Facturation */}
      <Route
        path="/billing"
        element={
          <ProtectedRoute allowedRoles={['transporteur', 'responsable_transporteur']}>
            <InvoiceList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/gps-history"
        element={
          <ProtectedRoute allowedRoles={['transporteur', 'responsable_transporteur']}>
            <GpsHistory />
          </ProtectedRoute>
        }
      />
      <Route
        path="/billing/create"
        element={
          <ProtectedRoute allowedRoles={['transporteur', 'responsable_transporteur']}>
            <InvoiceCreate />
          </ProtectedRoute>
        }
      />
      <Route
        path="/billing/automatic"
        element={
          <ProtectedRoute allowedRoles={['transporteur', 'responsable_transporteur']}>
            <AutomaticInvoice />
          </ProtectedRoute>
        }
      />

      {/* Vehicle Management Routes - Only accessible by transporteur */}
      <Route
        path="/vehicules"
        element={
          <ProtectedRoute allowedRoles={['transporteur', 'responsable_transporteur']}>
            <VehiculeList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/vehicules/create"
        element={
          <ProtectedRoute allowedRoles={['transporteur', 'responsable_transporteur']}>
            <VehiculeForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/vehicules/edit/:id"
        element={
          <ProtectedRoute allowedRoles={['transporteur', 'responsable_transporteur']}>
            <VehiculeForm />
          </ProtectedRoute>
        }
      />

      {/* Remorques Management Routes - Only accessible by transporteur */}
      <Route
        path="/remorques"
        element={
          <ProtectedRoute allowedRoles={['transporteur', 'responsable_transporteur']}>
            <RemorqueList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/remorques/create"
        element={
          <ProtectedRoute allowedRoles={['transporteur', 'responsable_transporteur']}>
            <RemorqueForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/remorques/edit/:id"
        element={
          <ProtectedRoute allowedRoles={['transporteur', 'responsable_transporteur']}>
            <RemorqueForm />
          </ProtectedRoute>
        }
      />

      {/* Mission Management Routes - Only accessible by transporteur */}
      <Route
        path="/missions"
        element={
          <ProtectedRoute allowedRoles={['transporteur', 'responsable_transporteur']}>
            <MissionList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/missions/create"
        element={
          <ProtectedRoute allowedRoles={['transporteur', 'responsable_transporteur']}>
            <MissionForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/missions/settings"
        element={
          <ProtectedRoute allowedRoles={['transporteur', 'responsable_transporteur']}>
            <MissionSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/missions/edit/:id"
        element={
          <ProtectedRoute allowedRoles={['transporteur', 'responsable_transporteur']}>
            <MissionForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/missions/assign"
        element={
          <ProtectedRoute allowedRoles={['transporteur', 'responsable_transporteur']}>
            <AssignMission />
          </ProtectedRoute>
        }
      />
      <Route
        path="/missions/assign/:missionId"
        element={
          <ProtectedRoute allowedRoles={['transporteur', 'responsable_transporteur']}>
            <AssignMission />
          </ProtectedRoute>
        }
      />
      <Route
        path="/missions/recurring"
        element={
          <ProtectedRoute allowedRoles={['transporteur', 'responsable_transporteur']}>
            <MissionTemplateList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/missions/recurring/create"
        element={
          <ProtectedRoute allowedRoles={['transporteur', 'responsable_transporteur']}>
            <MissionTemplateForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/missions/recurring/edit/:id"
        element={
          <ProtectedRoute allowedRoles={['transporteur', 'responsable_transporteur']}>
            <MissionTemplateForm />
          </ProtectedRoute>
        }
      />

      <Route
        path="/subcontract"
        element={
          <ProtectedRoute allowedRoles={['transporteur', 'responsable_transporteur']}>
            <SubcontractMission />
          </ProtectedRoute>
        }
      />

      <Route
        path="/subcontractors"
        element={
          <ProtectedRoute allowedRoles={['transporteur', 'responsable_transporteur']}>
            <Subcontractors />
          </ProtectedRoute>
        }
      />
      <Route
        path="/subcontractors/:id"
        element={
          <ProtectedRoute allowedRoles={['transporteur', 'responsable_transporteur']}>
            <SubcontractorDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/subcontracted-missions"
        element={
          <ProtectedRoute allowedRoles={['transporteur', 'responsable_transporteur']}>
            <SubcontractedMissions />
          </ProtectedRoute>
        }
      />
      <Route
        path="/subcontracted-missions/:missionId"
        element={
          <ProtectedRoute allowedRoles={['transporteur', 'responsable_transporteur']}>
            <SubcontractedMissionDetail />
          </ProtectedRoute>
        }
      />

      {/* Gantt Chart Route - Only accessible by transporteur */}
      <Route
        path="/gantt-chart"
        element={
          <ProtectedRoute allowedRoles={['transporteur', 'responsable_transporteur']}>
            <GanttChartPage />
          </ProtectedRoute>
        }
      />

      {/* Transporteur Client page (offers from expediteurs) */}
      <Route
        path="/client"
        element={
          <ProtectedRoute allowedRoles={['transporteur', 'responsable_transporteur']}>
            <ClientOffers />
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications"
        element={
          <ProtectedRoute allowedRoles={['transporteur', 'responsable_transporteur']}>
            <PendingOffers />
          </ProtectedRoute>
        }
      />
      <Route
        path="/offers/:id"
        element={
          <ProtectedRoute allowedRoles={['transporteur', 'responsable_transporteur']}>
            <OfferDetail />
          </ProtectedRoute>
        }
      />

      {/* Expediteur Mission Creation Route */}
      <Route
        path="/expediteur/missions/create"
        element={
          <ProtectedRoute allowedRoles={['expediteur']}>
            <MissionForm />
          </ProtectedRoute>
        }
      />

      {/* Expediteur Mission Edit Route */}
      <Route
        path="/expediteur/missions/edit/:id"
        element={
          <ProtectedRoute allowedRoles={['expediteur']}>
            <MissionForm />
          </ProtectedRoute>
        }
      />

      {/* Expediteur Mission List Route */}
      <Route
        path="/expediteur/missions"
        element={
          <ProtectedRoute allowedRoles={['expediteur']}>
            <MissionList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/expediteur/missions/:missionId/contract"
        element={
          <ProtectedRoute allowedRoles={['expediteur']}>
            <ContractView />
          </ProtectedRoute>
        }
      />

      <Route
        path="/expediteur/contracts"
        element={
          <ProtectedRoute allowedRoles={['expediteur']}>
            <ContractsList />
          </ProtectedRoute>
        }
      />

      {/* Expediteur transporteurs */}
      <Route
        path="/expediteur/transporteurs"
        element={
          <ProtectedRoute allowedRoles={['expediteur']}>
            <AddTransporteur />
          </ProtectedRoute>
        }
      />


      <Route
        path="/profile"
        element={
          <ProtectedRoute allowedRoles={['transporteur', 'responsable_transporteur', 'expediteur']}>
            <Profile />
          </ProtectedRoute>
        }
      />

      <Route
        path="/messagerie"
        element={
          <ProtectedRoute allowedRoles={['transporteur', 'responsable_transporteur']}>
            <MessagerieInterne />
          </ProtectedRoute>
        }
      />

      {/* Responsable transporteur management */}
      <Route
        path="/responsable/transporteurs"
        element={
          <ProtectedRoute allowedRoles={['responsable_transporteur']}>
            <ResponsableTransporteurs />
          </ProtectedRoute>
        }
      />

      {/* Reports routes */}
      <Route
        path="/reports/general"
        element={
          <ProtectedRoute allowedRoles={['transporteur', 'responsable_transporteur']}>
            <GeneralReports />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/subcontracting"
        element={
          <ProtectedRoute allowedRoles={['transporteur', 'responsable_transporteur']}>
            <SubcontractingReports />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/drivers"
        element={
          <ProtectedRoute allowedRoles={['transporteur', 'responsable_transporteur']}>
            <DriverReports />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/vehicles"
        element={
          <ProtectedRoute allowedRoles={['transporteur', 'responsable_transporteur']}>
            <VehicleReports />
          </ProtectedRoute>
        }
      />

      <Route
        path="/reports/billing"
        element={
          <ProtectedRoute allowedRoles={['transporteur', 'responsable_transporteur']}>
            <BillingReports />
          </ProtectedRoute>
        }
      />

      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/mentions-legales" element={<MentionsLegales />} />
      <Route path="/confidentialite" element={<Confidentialite />} />
      <Route path="/cgu" element={<CGU />} />
      <Route path="/solutions/planification-intelligente" element={<PlanificationIntelligente />} />
      <Route path="/solutions/suivi-temps-reel" element={<SuiviTempsReel />} />
      <Route path="/solutions/documents-centralises" element={<DocumentsCentralises />} />
      <Route path="/solutions/rapports-analytics" element={<RapportsAnalytics />} />
      <Route path="/solutions/gestion-ressources" element={<GestionRessources />} />
      <Route path="/solutions/ai-agent-center" element={<AiAgentCenter />} />
    </Routes >
  );
});

AppRoutes.displayName = 'AppRoutes';

function App() {
  // Best practice: Prevent routing tree from rendering until user auth is loaded
  const { loading } = useUser();

  if (loading) {
    return <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '100vh' }}>
      <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div>
    </div>;
  }

  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <AppRoutes />
    </Router>
  );
}

// Updated export to only wrap App in UserProvider at the export level
export default function RootApp() {
  return (
    <UserProvider>
      <FilterProvider>
        <App />
      </FilterProvider>
    </UserProvider>
  );
}

